import {getNumberFieldValidationMessage} from '@github-ui/memex-field-validators'
import {memo, useCallback} from 'react'

import {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import type {NumericValue} from '../../../api/columns/contracts/number'
import {parseNumber} from '../../../helpers/parsing'
import {useUpdateItem} from '../../../hooks/use-update-item'
import {type ColumnValue, hasValue} from '../../../models/column-value'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {RepoPicker} from '../../repo-picker'
import {useTableNavigationFocusInitialValue} from '../navigation'
import {NumberRenderer} from '../renderers/number-renderer'
import {TextCellEditor} from './text-cell-editor'
import {useConvertToIssueHelper} from './use-convert-to-issue-helper'
import {useSummaryAnchor} from './use-summary-anchor'

type Props = Readonly<{
  currentValue: ColumnValue<NumericValue>
  columnId: number
  rowId: string
  replaceContents: boolean
  model: MemexItemModel
  isIssueField: boolean
}>

export const NumberEditor: React.FC<Props> = memo(function NumberEditor(props) {
  const {currentValue, model, columnId, rowId, replaceContents, isIssueField} = props
  const initialValue = useTableNavigationFocusInitialValue()
  const renderButton = useCallback(() => {
    return <NumberRenderer currentValue={currentValue} model={model} />
  }, [currentValue, model])
  const {showConvertToIssue, onRowConvertSuccess} = useConvertToIssueHelper({model, columnId, isIssueField})
  const {renderAnchor, onOpenChange} = useSummaryAnchor({columnId, renderButton})

  const numberValue = hasValue(currentValue) ? currentValue.value : null
  const {updateItem} = useUpdateItem()

  const value = replaceContents || !numberValue ? initialValue : numberValue.value

  const handleUpdate = useCallback(
    (newValue: string) => {
      const prevNumberValue = numberValue?.value
      let numericValue: NumericValue | undefined = undefined

      if (newValue !== '') {
        const number = parseNumber(newValue)
        if (!Number.isNaN(number)) {
          numericValue = {value: number}
        } else if (prevNumberValue != null) {
          numericValue = {value: prevNumberValue}
        }
      }

      updateItem(model, {
        dataType: MemexColumnDataType.Number,
        memexProjectColumnId: columnId,
        value: numericValue,
        isIssueField,
      })

      return true
    },
    [columnId, model, numberValue?.value, updateItem, isIssueField],
  )

  if (showConvertToIssue) {
    return (
      <RepoPicker
        renderAnchor={renderAnchor}
        isOpen
        showPrompt
        item={model}
        onOpenChange={onOpenChange}
        onSuccess={onRowConvertSuccess}
      />
    )
  }

  return (
    <TextCellEditor
      model={model}
      columnId={columnId}
      rowId={rowId}
      defaultValue={value}
      onUpdate={handleUpdate}
      validationFn={getNumberFieldValidationMessage}
      align="right"
      emojiPickerDisabled
    />
  )
})

NumberEditor.displayName = 'NumberEditor'
